import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../utils/api';

const EMPTY = { name: '', email: '', password: '', customer_ref_id: '', role: 'customer' };

export default function CustomerLogins() {
  const [logins, setLogins]       = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);
  const PAGE_SIZE = 5;

  const [modal, setModal]         = useState(null); // null | 'add' | login-object
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pwdTarget, setPwdTarget]       = useState(null);  // login whose password is being reset
  const [newPwd, setNewPwd]             = useState('');
  const [pwdSaving, setPwdSaving]       = useState(false);
  const [pwdError, setPwdError]         = useState('');
  const [pwdDone, setPwdDone]           = useState(false);
  const [pending, setPending]           = useState([]);

  const loadLogins = useCallback(async () => {
    const data = await apiFetch('/users/customer-logins');
    setLogins(data);
  }, []);
  const loadPending = useCallback(async () => {
    try { setPending(await apiFetch('/auth/pending-registrations')); } catch (_) { setPending([]); }
  }, []);
  async function approvePending(id) {
    await apiFetch(`/auth/pending-registrations/${id}/approve`, { method: 'POST' });
    loadPending(); loadLogins();
  }
  async function rejectPending(id) {
    if (!window.confirm('Reject and delete this sign-up request?')) return;
    await apiFetch(`/auth/pending-registrations/${id}/reject`, { method: 'POST' });
    loadPending();
  }

  const loadCustomers = useCallback(async () => {
    const data = await apiFetch('/customers?limit=200');
    setCustomers(data.customers || []);
  }, []);

  useEffect(() => {
    Promise.all([loadLogins(), loadCustomers(), loadPending()]).finally(() => setLoading(false));
  }, [loadLogins, loadCustomers, loadPending]);

  // ── filtering ──────────────────────────────────────────────
  const filtered = logins.filter(l => {
    const q = search.toLowerCase();
    return !q || l.name.toLowerCase().includes(q) || l.email.toLowerCase().includes(q)
      || (l.customer_name || '').toLowerCase().includes(q);
  });

  // group by customer
  const grouped = filtered.reduce((acc, l) => {
    const key = l.customer_ref_id || '__none__';
    const label = l.customer_name || 'No Customer Linked';
    if (!acc[key]) acc[key] = { label, items: [] };
    acc[key].items.push(l);
    return acc;
  }, {});

  const groupEntries = Object.entries(grouped);
  const totalPages = Math.ceil(groupEntries.length / PAGE_SIZE);
  const pagedEntries = groupEntries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── modal helpers ──────────────────────────────────────────
  function openAdd(customerId = '') {
    setForm({ ...EMPTY, customer_ref_id: customerId, role: 'customer' });
    setError('');
    setModal('add');
  }

  function openEdit(login) {
    setForm({ name: login.name, email: login.email, password: '', customer_ref_id: login.customer_ref_id || '', role: login.role || 'customer' });
    setError('');
    setModal(login);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (modal !== 'add' && !payload.password) delete payload.password;
      if (modal === 'add') {
        await apiFetch('/users/customer-logins', { method: 'POST', body: payload });
      } else {
        await apiFetch(`/users/customer-logins/${modal.id}`, { method: 'PATCH', body: payload });
      }
      setModal(null);
      loadLogins();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function openResetPwd(login) {
    setPwdTarget(login);
    setNewPwd('');
    setPwdError('');
    setPwdDone(false);
  }

  async function handleResetPwd(e) {
    e.preventDefault();
    if (!newPwd.trim() || newPwd.length < 6) { setPwdError('Password must be at least 6 characters'); return; }
    setPwdSaving(true); setPwdError('');
    try {
      await apiFetch(`/users/customer-logins/${pwdTarget.id}`, { method: 'PATCH', body: { password: newPwd } });
      setPwdDone(true);
      setNewPwd('');
      setTimeout(() => setPwdTarget(null), 1500);
    } catch (err) {
      setPwdError(err.message);
    } finally {
      setPwdSaving(false);
    }
  }

  async function confirmDelete() {
    try {
      await apiFetch(`/users/customer-logins/${deleteTarget.id}`, { method: 'DELETE' });
      setDeleteTarget(null);
      loadLogins();
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><span className="spinner" /></div>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Customer Logins</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 2 }}>
            {logins.length} login{logins.length !== 1 ? 's' : ''} across {customers.length} customer{customers.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => openAdd()}>+ Add Login</button>
      </div>

      {/* Pending self-registrations */}
      {pending.length > 0 && (
        <div className="card" style={{ padding: 16, margin: '12px 0', borderLeft: '4px solid #f59e0b', background: 'var(--amber-50)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e', marginBottom: 10 }}>
            ⏳ Pending sign-up approvals ({pending.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {pending.map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '9px 12px' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{p.email}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button className="btn btn-primary btn-sm" style={{ background: '#0B6623' }} onClick={() => approvePending(p.id)}>✓ Approve</button>
                  <button className="btn btn-sm" style={{ background: 'var(--red-50)', color: '#b91c1c' }} onClick={() => rejectPending(p.id)}>Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: 24, marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <input className="input" style={{ maxWidth: 320 }} placeholder="Search by name, email or customer…"
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        {totalPages > 1 && (
          <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p} className="btn btn-sm"
                style={{ minWidth: 30, background: p === page ? 'var(--status-resolved-bg)' : 'transparent', color: p === page ? 'var(--status-resolved-text)' : 'var(--text-secondary)', border: `1px solid ${p === page ? 'var(--status-resolved-text)' : 'var(--border-subtle)'}`, fontWeight: p === page ? 700 : 400 }}
                onClick={() => setPage(p)}>{p}</button>
            ))}
          </div>
        )}
      </div>

      {logins.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔑</div>
          <p style={{ marginBottom: 4 }}>No customer logins yet.</p>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
            Add customer records in the Customers tab first, then create logins here.
          </p>
          <button className="btn btn-primary" onClick={() => openAdd()}>Add First Login</button>
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>No results for "{search}".</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {pagedEntries.map(([key, group]) => (
            <div key={key} className="card" style={{ overflowX: 'auto' }}>
              {/* Customer group header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', background: 'var(--bg-app)', borderBottom: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18 }}>🏢</span>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{group.label}</span>
                  <span style={{ background: 'var(--status-resolved-bg)', color: 'var(--status-resolved-text)', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999 }}>
                    {group.items.length} login{group.items.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => openAdd(key === '__none__' ? '' : key)}>
                  + Add Login
                </button>
              </div>

              {/* Logins table */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    {['Name', 'Email / Login', 'Role', 'Created', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '9px 20px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 500, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.items.map(login => (
                    <tr key={login.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '12px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--blue-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'var(--blue-700)', flexShrink: 0 }}>
                            {login.name?.[0]?.toUpperCase()}
                          </div>
                          <span style={{ fontWeight: 500 }}>{login.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 20px', color: 'var(--text-secondary)' }}>{login.email}</td>
                      <td style={{ padding: '12px 20px' }}>
                        {login.role === 'customer_manager'
                          ? <span style={{ background: '#ede9fe', color: '#6d28d9', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999 }}>Manager</span>
                          : <span style={{ background: 'var(--blue-100)', color: 'var(--blue-700)', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999 }}>Customer</span>
                        }
                      </td>
                      <td style={{ padding: '12px 20px', color: 'var(--text-secondary)', fontSize: 13 }}>
                        {login.created_at ? new Date(login.created_at).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td style={{ padding: '10px 20px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => openEdit(login)}>Edit</button>
                          <button className="btn btn-sm" style={{ background: 'var(--amber-50)', color: '#92400e', border: '1px solid var(--amber-100)', fontSize: 12 }} onClick={() => openResetPwd(login)}>🔑 Reset Pwd</button>
                          <button className="btn btn-sm" style={{ background: '#fee2e2', color: '#b91c1c' }} onClick={() => setDeleteTarget(login)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 24 }}
          onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div className="card" style={{ width: '100%', maxWidth: 480, padding: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700 }}>
                {modal === 'add' ? 'Add Customer Login' : `Edit — ${modal.name}`}
              </h2>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-secondary)', cursor: 'pointer' }}>×</button>
            </div>

            {error && (
              <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>
            )}

            <form onSubmit={handleSave}>
              {/* Customer selection */}
              <div style={{ background: 'var(--action-bg)', border: '1px solid var(--action-hover)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                <label className="label" style={{ fontSize: 12, marginBottom: 4 }}>Customer *</label>
                <select className="input" style={{ fontSize: 13, padding: '7px 10px' }}
                  value={form.customer_ref_id}
                  onChange={e => setForm(f => ({ ...f, customer_ref_id: e.target.value }))}
                  required>
                  <option value="">— Select Customer —</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.name}{c.city ? ` (${c.city})` : ''}</option>
                  ))}
                </select>
                {customers.length === 0 && (
                  <p style={{ fontSize: 12, color: '#ef4444', marginTop: 6 }}>
                    No customers found — add customers in the Customers tab first.
                  </p>
                )}
              </div>

              {/* Role */}
              <div style={{ marginBottom: 16 }}>
                <label className="label" style={{ fontSize: 12, marginBottom: 4 }}>Role *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { value: 'customer', label: '👤 Customer', desc: 'Can raise & view own tickets' },
                    { value: 'customer_manager', label: '🏢 Company Manager', desc: 'Can view all company tickets' },
                  ].map(opt => (
                    <label key={opt.value} style={{
                      flex: 1, display: 'flex', flexDirection: 'column', gap: 3,
                      border: `2px solid ${form.role === opt.value ? 'var(--status-resolved-text)' : 'var(--border-subtle)'}`,
                      borderRadius: 8, padding: '8px 12px', cursor: 'pointer',
                      background: form.role === opt.value ? 'var(--action-bg)' : 'var(--bg-surface)',
                      transition: 'all .12s',
                    }}>
                      <input type="radio" name="role" value={opt.value} checked={form.role === opt.value}
                        onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={{ display: 'none' }} />
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: form.role === opt.value ? 'var(--status-resolved-text)' : 'var(--text-muted)' }}>{opt.label}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{opt.desc}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Login details */}
              <div style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '12px 14px', marginBottom: 20 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .6, marginBottom: 10 }}>Login Details</p>

                <div style={{ marginBottom: 10 }}>
                  <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>Full Name *</label>
                  <input className="input" style={{ fontSize: 13, padding: '7px 10px' }} value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>Email / Login *</label>
                  <input className="input" type="email" style={{ fontSize: 13, padding: '7px 10px' }} value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
                </div>

                <div style={{ marginBottom: 0 }}>
                  <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>
                    Password {modal !== 'add' && <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(leave blank to keep current)</span>}
                    {modal === 'add' && ' *'}
                  </label>
                  <input className="input" type="password" style={{ fontSize: 13, padding: '7px 10px' }} value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    required={modal === 'add'}
                    placeholder={modal !== 'add' ? '••••••••' : 'Enter password'} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
                <button className="btn btn-primary" disabled={saving || customers.length === 0}>
                  {saving ? 'Saving…' : (modal === 'add' ? 'Create Login' : 'Save Changes')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {pwdTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 24 }}
          onClick={e => { if (e.target === e.currentTarget) setPwdTarget(null); }}>
          <div className="card" style={{ width: '100%', maxWidth: 420, padding: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 17, fontWeight: 700 }}>Reset Password</h2>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{pwdTarget.name} · {pwdTarget.email}</p>
              </div>
              <button onClick={() => setPwdTarget(null)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-secondary)', cursor: 'pointer' }}>×</button>
            </div>

            {pwdDone ? (
              <div style={{ background: 'var(--status-resolved-bg)', color: 'var(--status-resolved-text)', border: '1px solid var(--status-resolved-text)', borderRadius: 8, padding: '14px 16px', textAlign: 'center', fontWeight: 600, fontSize: 14 }}>
                ✓ Password reset successfully
              </div>
            ) : (
              <form onSubmit={handleResetPwd}>
                {pwdError && (
                  <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{pwdError}</div>
                )}
                <div style={{ marginBottom: 20 }}>
                  <label className="label" style={{ fontSize: 12, marginBottom: 4 }}>New Password *</label>
                  <input
                    className="input"
                    type="password"
                    placeholder="Minimum 6 characters"
                    value={newPwd}
                    onChange={e => { setNewPwd(e.target.value); setPwdError(''); }}
                    autoFocus
                    required
                  />
                  <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 5 }}>
                    The customer will use this password on their next login.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setPwdTarget(null)}>Cancel</button>
                  <button className="btn btn-primary" disabled={pwdSaving}>
                    {pwdSaving ? 'Saving…' : 'Set New Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div className="card" style={{ padding: 32, maxWidth: 420, width: '100%' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Delete Login?</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 6 }}>
              This will permanently delete the login for <strong>{deleteTarget.name}</strong>
              {deleteTarget.customer_name && <> ({deleteTarget.customer_name})</>}.
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 24 }}>
              Their submitted tickets will remain but they will no longer be able to log in.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
